import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Stack } from "@similarweb/ui-components/foundations";

import { INDUSTRIES_CODE_NAMES, type IndustryCodeDto } from "app/pages/sales-intelligence/domain/industry/types/codes";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";

import { MAX_INDUSTRY_BULK_VALUES, parseIndustryBulkValue } from "./utils";

import {
    StyledBulkCapError,
    StyledBulkInputText,
    StyledCounter,
    StyledPositionRelative,
    StyledTextarea,
    StyledTextareaContainer,
} from "./styles";

type IndustryCodesBulkInputProps = {
    codeType: IndustryCodeDto;
    value: string;
    onChange: (value: string) => void;
};

const IndustryCodesBulkInput = ({ codeType, value, onChange }: IndustryCodesBulkInputProps) => {
    const { translate } = useTranslate();
    const [isFocused, setIsFocused] = useState(false);

    const parsedRawValue = useMemo(() => parseIndustryBulkValue(value), [value]);
    const isOverBulkLimit = parsedRawValue.length > MAX_INDUSTRY_BULK_VALUES;

    const textTranslatable = leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalText(
        INDUSTRIES_CODE_NAMES[codeType],
    );

    return (
        <Stack
            gap="16px"
            width="100%"
            data-automation="industry-codes-bulk-input"
        >
            <StyledBulkInputText>{translate(textTranslatable.key, textTranslatable.replacements)}</StyledBulkInputText>

            <StyledPositionRelative>
                <StyledTextareaContainer focus={isFocused}>
                    <StyledTextarea
                        autoFocus
                        placeholder={translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalPlaceholder())}
                        onChange={(event) => onChange(event.target.value)}
                        value={value}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        data-automation="industry-codes-bulk-textarea"
                    />
                </StyledTextareaContainer>
                <StyledCounter isFull={parsedRawValue.length >= MAX_INDUSTRY_BULK_VALUES}>
                    {Math.min(parsedRawValue.length, MAX_INDUSTRY_BULK_VALUES)}/{MAX_INDUSTRY_BULK_VALUES}{" "}
                    {INDUSTRIES_CODE_NAMES[codeType]}{" "}
                    {translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalCounter())}
                </StyledCounter>
            </StyledPositionRelative>
            {isOverBulkLimit ? (
                <StyledBulkCapError data-automation="industry-codes-bulk-limit-error">
                    {translate(leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddModalMaxExceeded(), {
                        max: MAX_INDUSTRY_BULK_VALUES,
                    })}
                </StyledBulkCapError>
            ) : null}
        </Stack>
    );
};

export default IndustryCodesBulkInput;
